/**
 * Phase 1: client-side derived metrics on summarized report data.
 * Recipes from report.config.formula_fields, derived_metrics, and/or chart.derivedMetrics.
 */

import { compareReportGroupBucketKeys } from '../components/reports/matrix/matrixDateGrouping';
import { compareReportGroupKeys as compareReportGroupKeysFull } from '../components/reports/charts/chartPicklistColors';
import { buildMatrixGrid } from '../components/reports/matrix/matrixPivot';
import { buildMeasuresFromSummarized } from '../components/reports/matrix/matrixReportConfig';
import {
  getReportGroupBucket,
  getRawFieldValueFromRow,
  resolvePicklistDisplayForFieldValue,
} from '../components/reports/matrix/matrixDateGrouping';
import { resolvePicklistValuesForGroupField } from '../components/reports/charts/chartPicklistColors';
import { getAggregationNumericResult } from './reportCurrencyAggregate';
import {
  collectFormulaFieldsFromConfig,
  formulaFieldToRecipe,
  FORMULA_SOURCE_KINDS,
  encodeFormulaSourceKey,
  buildWinRateFieldName,
} from './formulaFields';

export const DERIVED_METRIC_TYPES = {
  PERIOD_OVER_PERIOD: 'period_over_period',
  WIN_RATE: 'win_rate',
};

function parseReportConfig(config) {
  if (config == null) return {};
  if (typeof config === 'string') {
    try {
      const o = JSON.parse(config);
      return typeof o === 'object' && o !== null ? o : {};
    } catch {
      return {};
    }
  }
  return typeof config === 'object' ? { ...config } : {};
}

function parseNumeric(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'object' && value !== null) {
    return parseNumeric(value.value ?? value.displayValue);
  }
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function roundTo(value, decimals = 1) {
  if (value == null || !Number.isFinite(value)) return value;
  const f = 10 ** (decimals ?? 1);
  return Math.round(value * f) / f;
}

/** Sort group keys (dates chronologically, picklists by configured sort_order). */
export function compareGroupKeys(a, b, groupField = null, reportFields = null) {
  if (groupField) {
    return compareReportGroupKeysFull(a, b, groupField, reportFields);
  }
  return compareReportGroupBucketKeys(a, b);
}

/**
 * Numeric measure values per group category from flat and/or nested summarized data.
 */
export function extractMeasureSeriesFromDataset(
  dataset,
  sourceFieldName,
  groupFieldName = null
) {
  const out = {};
  if (!dataset || !sourceFieldName) return out;

  const flat = dataset[sourceFieldName];
  if (flat && typeof flat === 'object' && !Array.isArray(flat)) {
    let hasNumeric = false;
    Object.entries(flat).forEach(([cat, val]) => {
      if (cat === '_metadata') return;
      const n = parseNumeric(val);
      if (n != null) {
        out[cat] = n;
        hasNumeric = true;
      }
    });
    if (hasNumeric && Object.keys(out).length > 0) {
      return out;
    }
  }

  const gf =
    groupFieldName ||
    resolveGroupFieldName(dataset, {
      groupField: groupFieldName,
      sourceField: sourceFieldName,
    });
  const groupBlock = gf ? dataset[gf] : null;
  if (groupBlock && typeof groupBlock === 'object') {
    Object.keys(groupBlock).forEach((cat) => {
      if (cat === '_metadata') return;
      const cell = groupBlock[cat]?.[sourceFieldName];
      const n = parseNumeric(cell?.value ?? cell);
      if (n != null) out[cat] = n;
    });
  }

  return out;
}

export function sortGroupKeys(keys, groupField = null, reportFields = null) {
  return [...(keys || [])].sort((a, b) =>
    compareGroupKeys(a, b, groupField, reportFields)
  );
}

export function growthMetricId(sourceField) {
  const base = String(sourceField || 'measure').replace(/[^a-zA-Z0-9_]/g, '_');
  return `${base}_growth_pct`;
}

export function winRateMetricId(numeratorKey, denominatorKey) {
  const slug = (s) =>
    String(s || 'measure')
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .slice(0, 48);
  return `win_rate_${slug(numeratorKey)}_${slug(denominatorKey)}`;
}

/**
 * Build a period-over-period growth % recipe from chart axes.
 */
export function buildPeriodOverPeriodRecipe({
  sourceField,
  groupField,
  label,
  firstPeriodValue = 0,
  decimals = 1,
  id,
}) {
  if (!sourceField || !groupField) return null;
  const outputId = id || growthMetricId(sourceField);
  return {
    id: outputId,
    label: label || 'Growth %',
    type: DERIVED_METRIC_TYPES.PERIOD_OVER_PERIOD,
    sourceField,
    groupField,
    firstPeriodValue,
    decimals,
  };
}

function normalizeRecipe(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const type = raw.type || DERIVED_METRIC_TYPES.PERIOD_OVER_PERIOD;
  const groupField =
    raw.groupField ||
    raw.group_field ||
    raw.groupMeasure?.field_name ||
    raw.group_field?.field_name ||
    null;

  if (type === DERIVED_METRIC_TYPES.WIN_RATE || raw.formulaKey === 'win_rate') {
    const mapOperand = (op) => {
      if (!op) return null;
      if (op.fieldName) return op;
      if (op.field_name) {
        return {
          fieldName: op.field_name,
          fieldLabel: op.field_label,
          objectName: op.object_name,
          aggregation: op.aggregation || 'sum',
          sourceKind: op.source_kind || FORMULA_SOURCE_KINDS.MEASURE,
          picklistGroupField: op.picklist_group_field || null,
          picklistValue: op.picklist_value ?? null,
        };
      }
      return null;
    };

    let numerators = Array.isArray(raw.numerators)
      ? raw.numerators.map(mapOperand).filter(Boolean)
      : [];
    let denominators = Array.isArray(raw.denominators)
      ? raw.denominators.map(mapOperand).filter(Boolean)
      : [];

    if (!numerators.length) {
      const single = mapOperand(raw.numerator || raw.numerator_source);
      if (single) numerators = [single];
    }
    if (!denominators.length) {
      const single = mapOperand(raw.denominator || raw.denominator_source);
      if (single) denominators = [single];
    }

    if (!numerators.length || !denominators.length) return null;

    const id =
      raw.id ||
      raw.output?.field_name ||
      buildWinRateFieldName(numerators, denominators);

    return {
      id,
      label: raw.label || raw.output?.label || 'Win Rate %',
      type: DERIVED_METRIC_TYPES.WIN_RATE,
      groupField,
      numerators,
      denominators,
      numerator: numerators[0],
      denominator: denominators[0],
      decimals: raw.decimals ?? 1,
      divideByZero: raw.divideByZero || raw.divide_by_zero || 'null',
    };
  }

  const sourceField = raw.sourceField || raw.source_field || raw.sourceMeasure?.field_name;
  const id = raw.id || raw.output?.field_name || (sourceField ? growthMetricId(sourceField) : null);
  if (!id || !sourceField) return null;
  return {
    id,
    label: raw.label || raw.output?.label || 'Growth %',
    type,
    sourceField,
    groupField,
    firstPeriodValue:
      raw.firstPeriodValue !== undefined
        ? raw.firstPeriodValue
        : raw.first_period_value !== undefined
          ? raw.first_period_value
          : null,
    decimals: raw.decimals ?? 1,
    divideByZero: raw.divideByZero || raw.divide_by_zero || 'zero',
  };
}

/** Merge formula fields, report derived_metrics, and chart recipes (later wins on duplicate id). */
export function collectDerivedMetricRecipes({
  reportConfig,
  charts = [],
  chartConfig = null,
  formulaFields = null,
  defaultGroupFieldName = null,
} = {}) {
  const cfg = parseReportConfig(reportConfig);
  const fromReport = Array.isArray(cfg.derived_metrics) ? cfg.derived_metrics : [];
  const map = new Map();

  const fromFormulas =
    formulaFields ||
    collectFormulaFieldsFromConfig(reportConfig);
  fromFormulas.forEach((ff) => {
    const recipe = formulaFieldToRecipe(ff, defaultGroupFieldName);
    const n = normalizeRecipe(recipe);
    if (n) map.set(n.id, n);
  });

  fromReport.forEach((r) => {
    const n = normalizeRecipe(r);
    if (n) map.set(n.id, n);
  });

  const chartList = chartConfig
    ? [chartConfig, ...charts.filter((c) => c?.id !== chartConfig?.id)]
    : charts;

  chartList.forEach((chart) => {
    const list = Array.isArray(chart?.derivedMetrics) ? chart.derivedMetrics : [];
    list.forEach((r) => {
      const n = normalizeRecipe(r);
      if (n) map.set(n.id, n);
    });
  });

  return [...map.values()];
}

function resolveGroupFieldName(dataset, recipe) {
  if (recipe.groupField && dataset[recipe.groupField]) {
    return recipe.groupField;
  }
  const meta = dataset._metadata?.group_fields || {};
  const keys = Object.keys(meta);
  if (recipe.groupField && keys.includes(recipe.groupField)) {
    return recipe.groupField;
  }
  if (keys.length === 1) return keys[0];
  return recipe.groupField || keys[0] || null;
}

function computePeriodOverPeriod(sourceMap, options = {}) {
  const {
    firstPeriodValue = 0,
    decimals = 1,
    divideByZero = 'zero',
    recipe = null,
    dataset = null,
  } = options;
  const groupMeta =
    recipe?.groupField && dataset?._metadata?.group_fields
      ? dataset._metadata.group_fields[recipe.groupField]
      : null;
  const categories = sortGroupKeys(
    Object.keys(sourceMap || {}),
    groupMeta || (recipe?.groupField ? { field_name: recipe.groupField } : null)
  );
  const out = {};

  categories.forEach((cat, index) => {
    const current = parseNumeric(sourceMap[cat]);
    if (index === 0) {
      out[cat] =
        firstPeriodValue == null ? null : roundTo(firstPeriodValue, decimals);
      return;
    }
    const prevCat = categories[index - 1];
    const previous = parseNumeric(sourceMap[prevCat]);
    if (current == null || previous == null) {
      out[cat] = null;
      return;
    }
    if (previous === 0) {
      out[cat] =
        divideByZero === 'null'
          ? null
          : roundTo(divideByZero === 'na' ? 0 : firstPeriodValue, decimals);
      return;
    }
    const pct = ((current - previous) / previous) * 100;
    out[cat] = roundTo(pct, decimals);
  });

  return out;
}

/** Sum numeric values per category across multiple measure series. */
function sumSeriesMaps(maps) {
  const out = {};
  (maps || []).forEach((map) => {
    Object.entries(map || {}).forEach(([cat, val]) => {
      const n = parseNumeric(val);
      if (n == null) return;
      out[cat] = (out[cat] ?? 0) + n;
    });
  });
  return out;
}

function extractCombinedOperandSeries(dataset, operands, groupFieldName, runtime) {
  const maps = (operands || []).map((op) =>
    extractMeasureSeriesForSource(dataset, op, groupFieldName, runtime)
  );
  return sumSeriesMaps(maps);
}

function aggregateOperandsGrandTotal(previewData, operands, runtime) {
  if (!previewData?.length || !operands?.length) return null;
  let total = 0;
  let has = false;
  operands.forEach((op) => {
    let rows = previewData;
    if (op?.sourceKind === FORMULA_SOURCE_KINDS.PICKLIST_SLICE) {
      rows = previewData.filter((row) => rowMatchesPicklistSlice(row, op, runtime));
    }
    let n = null;
    if (
      op?.sourceKind === FORMULA_SOURCE_KINDS.COUNT ||
      String(op?.aggregation || '').toLowerCase() === 'count'
    ) {
      n = rows.length;
      has = true;
    } else {
      n = aggregateRowsForSource(
        rows,
        op,
        runtime.selectedFields,
        runtime.summarizedFields
      );
      if (n != null) has = true;
    }
    if (n != null) total += n;
  });
  return has ? total : null;
}

function collectRowGroupLabels(previewData, rowGroupField, dateOptions = {}) {
  const labels = new Set();
  if (!previewData?.length || !rowGroupField?.field_name) return labels;
  previewData.forEach((row) => {
    const { label } = getReportGroupBucket(rowGroupField, row, dateOptions);
    if (label != null && String(label).trim() !== '') {
      labels.add(String(label).trim());
    }
  });
  return labels;
}

function computeWinRate(numeratorMap, denominatorMap, options = {}) {
  const { decimals = 1, divideByZero = 'null', allCategories = null } = options;
  const categories = [
    ...new Set([
      ...(allCategories || []),
      ...Object.keys(numeratorMap || {}),
      ...Object.keys(denominatorMap || {}),
    ]),
  ].filter((c) => c && !String(c).startsWith('_'));

  const out = {};

  categories.forEach((cat) => {
    const numRaw = parseNumeric(numeratorMap?.[cat]);
    const denRaw = parseNumeric(denominatorMap?.[cat]);
    const num = numRaw != null ? numRaw : 0;
    const den = denRaw != null ? denRaw : 0;

    if (denRaw == null && numRaw == null) {
      out[cat] = null;
      return;
    }
    if (den === 0) {
      out[cat] =
        divideByZero === 'null'
          ? null
          : roundTo(0, decimals);
      return;
    }
    out[cat] = roundTo((num / den) * 100, decimals);
  });

  return out;
}

function aggregateRowsForSource(rows, source, selectedFields, summarizedFields) {
  if (!rows?.length || !source?.fieldName) return null;

  const field =
    (selectedFields || []).find((f) => f.field_name === source.fieldName) || {
      field_name: source.fieldName,
      field_label: source.fieldLabel,
      object_name: source.objectName,
      data_type: 'number',
    };

  const aggregation =
    source.sourceKind === FORMULA_SOURCE_KINDS.COUNT
      ? 'count'
      : source.aggregation || 'sum';

  return getAggregationNumericResult(rows, field, aggregation, null, null);
}

function findGroupFieldDef(fieldName, runtime = {}) {
  const { rowGroups = [], columnGroups = [], selectedGroups = [], selectedFields = [] } =
    runtime;
  return (
    [...rowGroups, ...columnGroups, ...selectedGroups, ...selectedFields].find(
      (f) => f.field_name === fieldName
    ) || { field_name: fieldName }
  );
}

function rowMatchesPicklistSlice(row, source, runtime = {}) {
  if (!source?.picklistGroupField || source.picklistValue === '__all__') {
    return true;
  }
  const picklistField = findGroupFieldDef(source.picklistGroupField, runtime);
  const picklistValues = resolvePicklistValuesForGroupField(
    picklistField,
    runtime.selectedFields
  );
  const dateOptions = runtime.dateOptions || {};
  const resolved = resolvePicklistDisplayForFieldValue(
    getRawFieldValueFromRow(row, picklistField),
    picklistField
  );
  const rowTokens = new Set(
    [resolved.key, resolved.label, resolved.rawKey]
      .filter((v) => v != null && String(v).trim() !== '')
      .map((v) => String(v).trim().toLowerCase())
  );

  const target = String(source.picklistValue || '').trim().toLowerCase();
  if (rowTokens.has(target)) return true;

  const pvMatch = (picklistValues || []).find((pv) => {
    const label = String(
      pv.value_label || pv.label || pv.value || ''
    )
      .trim()
      .toLowerCase();
    const key = String(pv.value_key || pv.key || '')
      .trim()
      .toLowerCase();
    return label === target || key === target;
  });
  if (!pvMatch) return false;

  const label = String(
    pvMatch.value_label || pvMatch.label || pvMatch.value || ''
  )
    .trim()
    .toLowerCase();
  const key = String(pvMatch.value_key || pvMatch.key || '')
    .trim()
    .toLowerCase();
  return rowTokens.has(label) || rowTokens.has(key);
}

/** Summary/matrix: measure or count per row group, filtered by a picklist group value. */
function extractPicklistFilteredSeriesFromPreview(
  previewData,
  source,
  rowGroupField,
  runtime = {}
) {
  const out = {};
  if (!previewData?.length || !source?.picklistGroupField) return out;

  const dateOptions = runtime.dateOptions || {};
  const filtered = previewData.filter((row) =>
    rowMatchesPicklistSlice(row, source, runtime)
  );

  if (!rowGroupField?.field_name) {
    const total = aggregateRowsForSource(
      filtered,
      source,
      runtime.selectedFields,
      runtime.summarizedFields
    );
    if (total != null) out.__all__ = total;
    return out;
  }

  const buckets = new Map();
  filtered.forEach((row) => {
    const { label } = getReportGroupBucket(rowGroupField, row, dateOptions);
    const key = label || 'N/A';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  });

  const isCount =
    source.sourceKind === FORMULA_SOURCE_KINDS.COUNT ||
    String(source.aggregation || '').toLowerCase() === 'count';

  buckets.forEach((rows, key) => {
    let n = null;
    if (isCount) {
      n = rows.length;
    } else {
      n = aggregateRowsForSource(
        rows,
        source,
        runtime.selectedFields,
        runtime.summarizedFields
      );
    }
    if (n != null) out[key] = n;
  });

  if (rowGroupField?.field_name) {
    const allLabels = collectRowGroupLabels(
      previewData,
      rowGroupField,
      dateOptions
    );
    allLabels.forEach((label) => {
      if (!Object.prototype.hasOwnProperty.call(out, label) && isCount) {
        out[label] = 0;
      }
    });
  }

  return out;
}

function extractCountSeriesFromPreview(
  previewData,
  source,
  groupField,
  dateOptions
) {
  const out = {};
  if (!previewData?.length || !source?.fieldName) return out;

  if (!groupField?.field_name) {
    const total = aggregateRowsForSource(
      previewData,
      source,
      [],
      {}
    );
    if (total != null) out.__all__ = total;
    return out;
  }

  const buckets = new Map();
  previewData.forEach((row) => {
    const { label } = getReportGroupBucket(groupField, row, dateOptions);
    const key = label || 'N/A';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  });

  buckets.forEach((rows, key) => {
    const n = aggregateRowsForSource(rows, source, [], {});
    if (n != null) out[key] = n;
  });

  return out;
}

function columnKeyMatchesPicklist(colKey, colMeta, picklistValue) {
  if (picklistValue === '__all__') return true;
  const target = String(picklistValue || '').trim().toLowerCase();
  const candidates = new Set();
  const add = (v) => {
    if (v != null && String(v).trim() !== '') {
      candidates.add(String(v).trim().toLowerCase());
    }
  };
  add(colKey);
  if (colMeta) {
    add(colMeta.label);
    (colMeta.parts || []).forEach(add);
  }
  return candidates.has(target);
}

function extractPicklistSliceSeriesFromMatrix(
  previewData,
  source,
  rowGroupField,
  rowGroups,
  columnGroups,
  selectedFields,
  summarizedFields,
  matrixDateGranularity
) {
  const out = {};
  if (!previewData?.length || !source?.picklistGroupField) return out;

  const measures = buildMeasuresFromSummarized(selectedFields, summarizedFields);
  const measure = measures.find((m) => m.field_name === source.fieldName);
  if (!measure) {
    return extractPicklistFilteredSeriesFromPreview(
      previewData,
      source,
      rowGroupField,
      {
        previewData,
        selectedFields,
        summarizedFields,
        rowGroups,
        columnGroups,
        matrixDateGranularity,
        dateOptions: { matrixDateGranularity },
      }
    );
  }

  const grid = buildMatrixGrid(
    previewData,
    rowGroups,
    columnGroups,
    measures,
    { matrixDateGranularity, reportFields: selectedFields }
  );

  const sliceOnColumns = columnGroups.some(
    (f) => f.field_name === source.picklistGroupField
  );
  const sliceOnRows = rowGroups.some(
    (f) => f.field_name === source.picklistGroupField
  );

  const mid = measure.id || measure.field_name;
  const allColumns =
    source.picklistValue === '__all__'
      ? grid.columnKeys || []
      : (grid.columnKeys || []).filter((ck) =>
          columnKeyMatchesPicklist(
            ck,
            grid.columnKeyMeta?.[ck],
            source.picklistValue
          )
        );

  if (sliceOnColumns && rowGroupField) {
    (grid.rowKeys || []).forEach((rowKey) => {
      let sum = 0;
      let has = false;
      allColumns.forEach((colKey) => {
        const v = parseNumeric(grid.cells?.[rowKey]?.[colKey]?.[mid]);
        if (v != null) {
          sum += v;
          has = true;
        }
      });
      if (has) {
        const label =
          grid.rowLabels?.find((r) => r.key === rowKey)?.label || rowKey;
        out[label] = sum;
      }
    });
    return out;
  }

  if (sliceOnRows && rowGroupField) {
    const rowKeysForSlice =
      source.picklistValue === '__all__'
        ? grid.rowKeys || []
        : (grid.rowKeys || []).filter((rk) =>
            columnKeyMatchesPicklist(
              rk,
              grid.rowKeyMeta?.[rk],
              source.picklistValue
            )
          );

    (grid.columnKeys || []).forEach((colKey) => {
      let sum = 0;
      let has = false;
      rowKeysForSlice.forEach((rowKey) => {
        const v = parseNumeric(grid.cells?.[rowKey]?.[colKey]?.[mid]);
        if (v != null) {
          sum += v;
          has = true;
        }
      });
      if (has) {
        const label =
          grid.columnLabels?.find((c) => c.key === colKey)?.label || colKey;
        out[label] = sum;
      }
    });
    return out;
  }

  return out;
}

function extractMeasureSeriesForSource(
  dataset,
  source,
  groupFieldName,
  runtime = {}
) {
  if (!source?.fieldName) return {};

  if (source.sourceKind === FORMULA_SOURCE_KINDS.PICKLIST_SLICE) {
    const { rowGroups = [], columnGroups = [] } = runtime;
    const rowGroupField =
      (rowGroups || []).find((f) => f.field_name === groupFieldName) ||
      rowGroups[0] ||
      null;

    const fromSummary = extractPicklistFilteredSeriesFromPreview(
      runtime.previewData,
      source,
      rowGroupField,
      runtime
    );
    if (Object.keys(fromSummary).length > 0) {
      return fromSummary;
    }

    if (rowGroups?.length && columnGroups?.length) {
      return extractPicklistSliceSeriesFromMatrix(
        runtime.previewData,
        source,
        rowGroupField,
        rowGroups,
        columnGroups,
        runtime.selectedFields,
        runtime.summarizedFields,
        runtime.matrixDateGranularity
      );
    }
    return fromSummary;
  }

  if (source.sourceKind === FORMULA_SOURCE_KINDS.COUNT) {
    const groupField =
      groupFieldName && dataset?._metadata?.group_fields?.[groupFieldName]
        ? { field_name: groupFieldName, ...dataset._metadata.group_fields[groupFieldName] }
        : runtime.selectedGroups?.[0] || null;
    return extractCountSeriesFromPreview(
      runtime.previewData,
      source,
      groupField,
      runtime.dateOptions
    );
  }

  return extractMeasureSeriesFromDataset(dataset, source.fieldName, groupFieldName);
}

function attachMeasureToDataset(dataset, outputId, valuesByCategory, recipe) {
  if (!dataset || !outputId || !valuesByCategory) return;

  dataset[outputId] = { ...valuesByCategory };

  if (!dataset._metadata) {
    dataset._metadata = {
      group_fields: {},
      summarized_fields: {},
      report_info: {},
    };
  }
  if (!dataset._metadata.summarized_fields) {
    dataset._metadata.summarized_fields = {};
  }

  dataset._metadata.summarized_fields[outputId] = {
    field_name: outputId,
    field_label: recipe.label || 'Growth %',
    data_type: 'percent',
    aggregation: 'derived',
    is_derived: true,
    derived_type: recipe.type,
    source_field: recipe.sourceField || recipe.denominator?.fieldName,
  };

  const groupFieldName = resolveGroupFieldName(dataset, recipe);
  if (!groupFieldName) return;

  Object.keys(valuesByCategory).forEach((cat) => {
    if (!dataset[groupFieldName]) dataset[groupFieldName] = {};
    if (!dataset[groupFieldName][cat]) dataset[groupFieldName][cat] = {};
    dataset[groupFieldName][cat][outputId] = {
      value: valuesByCategory[cat],
    };
  });
}

/**
 * Apply all derived metric recipes to a summarized dataset (mutates copy).
 * @param {object} [runtime] — previewData, row/column groups for win-rate picklist slices
 */
export function applyDerivedMetricsToDataset(dataset, recipes, runtime = {}) {
  if (!dataset || !Array.isArray(recipes) || recipes.length === 0) {
    return dataset;
  }

  const next = { ...dataset };
  if (dataset._metadata) {
    next._metadata = {
      ...dataset._metadata,
      group_fields: { ...(dataset._metadata.group_fields || {}) },
      summarized_fields: { ...(dataset._metadata.summarized_fields || {}) },
      report_info: { ...(dataset._metadata.report_info || {}) },
    };
  }

  recipes.forEach((rawRecipe) => {
    const recipe = normalizeRecipe(rawRecipe);
    if (!recipe) return;

    if (recipe.type === DERIVED_METRIC_TYPES.WIN_RATE) {
      const groupFieldName = resolveGroupFieldName(next, recipe);
      const numerators =
        recipe.numerators?.length > 0
          ? recipe.numerators
          : recipe.numerator
            ? [recipe.numerator]
            : [];
      const denominators =
        recipe.denominators?.length > 0
          ? recipe.denominators
          : recipe.denominator
            ? [recipe.denominator]
            : [];

      const numMap = extractCombinedOperandSeries(
        next,
        numerators,
        groupFieldName,
        runtime
      );
      const denMap = extractCombinedOperandSeries(
        next,
        denominators,
        groupFieldName,
        runtime
      );

      const rowGroupField =
        (runtime.rowGroups || runtime.selectedGroups || []).find(
          (f) => f.field_name === groupFieldName
        ) ||
        runtime.rowGroups?.[0] ||
        runtime.selectedGroups?.[0] ||
        null;

      const allCategories = collectRowGroupLabels(
        runtime.previewData,
        rowGroupField,
        runtime.dateOptions || {}
      );

      const values = computeWinRate(numMap, denMap, {
        decimals: recipe.decimals,
        divideByZero: recipe.divideByZero,
        allCategories: [...allCategories],
      });

      if (runtime.previewData?.length) {
        const numTotal = aggregateOperandsGrandTotal(
          runtime.previewData,
          numerators,
          runtime
        );
        const denTotal = aggregateOperandsGrandTotal(
          runtime.previewData,
          denominators,
          runtime
        );
        if (numTotal != null && denTotal != null) {
          if (denTotal === 0) {
            values.__all__ =
              recipe.divideByZero === 'null' ? null : roundTo(0, recipe.decimals);
          } else {
            values.__all__ = roundTo(
              (numTotal / denTotal) * 100,
              recipe.decimals
            );
          }
        }
      }

      attachMeasureToDataset(next, recipe.id, values, recipe);
      return;
    }

    const sourceMap = extractMeasureSeriesFromDataset(
      next,
      recipe.sourceField,
      recipe.groupField
    );
    if (Object.keys(sourceMap).length === 0) {
      return;
    }

    if (recipe.type === DERIVED_METRIC_TYPES.PERIOD_OVER_PERIOD) {
      const values = computePeriodOverPeriod(sourceMap, {
        firstPeriodValue: recipe.firstPeriodValue,
        decimals: recipe.decimals,
        divideByZero: recipe.divideByZero,
        recipe,
        dataset: next,
      });
      attachMeasureToDataset(next, recipe.id, values, recipe);
    }
  });

  return next;
}

export function parseReportConfigDerivedMetrics(reportConfig) {
  const cfg = parseReportConfig(reportConfig);
  return Array.isArray(cfg.derived_metrics) ? cfg.derived_metrics : [];
}
